/**
 * ScoreEntrySection — score matrix for the Scoring tab. Criteria run down the
 * Y axis, sites run across the X axis, and a Total row sits at the top,
 * directly under the site names and above the first criterion.
 *
 *   ┌───────────┬────────┬────────┬────────┐
 *   │ Criterion │ Site A │ Site B │ Site C │
 *   ├───────────┼────────┼────────┼────────┤
 *   │ TOTAL     │  7.4   │  6.1   │  8.2   │  ← summary band
 *   ├───────────┼────────┼────────┼────────┤
 *   │ Access    │ [ 8 ]  │ [ 6 ]  │ [ 9 ]  │  ← editable band
 *   │ Visibility│ [ 7 ]  │ [ 6 ]  │ [ 8 ]  │
 *   └───────────┴────────┴────────┴────────┘
 *
 * Each cell is a numeric input (1–10, step 0.5). Changes debounce 500ms before
 * dispatching `upsertScore`, so rapid typing doesn't generate a write per
 * keystroke.
 *
 * Built as a CSS grid using the idiom from comparison/ComparisonScoring.tsx
 * (one `grid` div, `display: contents` per logical row, sticky row labels)
 * rather than a <table>, because a table in this orientation can't pin its
 * first column and its header row independently.
 *
 * Linear: MYD-207 (E2).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Card, CardHeader, CardContent } from 'components/ui/card'
import { Input } from 'components/ui/input'
import { BarChart3, Sparkles } from 'lucide-react'
import { cn } from 'lib/utils'
import { upsertScore, fetchScoringSummary } from 'slices/scoringSlice'
import { isAskMdtAvailable } from 'features/AskMdt/availability'
import { ScoreSuggestionsSheet, type ScoreToApply } from 'features/AskMdt/components/ScoreSuggestionsSheet'

const DEBOUNCE_MS = 500

// Tunable layout constant. SITE_COL_MIN is driven by the input (w-16 = 64px)
// plus its cell padding; below ~80px Chrome clips the number spinner and the
// value together. The label column width lives on --label-w below.
const SITE_COL_MIN = 84

interface ScoreEntrySectionProps {
	evalId: string
}

const SectionHeader = ({ saveError }: { saveError?: string | null }) => (
	<CardHeader className="pb-3">
		<h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-1.5">
			<BarChart3 className="w-4 h-4" />
			Score entry
		</h4>
		<p className="text-xs text-gray-500 mt-1">
			Score each site against each criterion (1–10, step 0.5). Edits save automatically ~500ms after you stop typing.
		</p>
		{saveError && (
			<p className="text-xs text-destructive mt-1" role="alert">
				{saveError}
			</p>
		)}
	</CardHeader>
)

const ScoreEntrySection = ({ evalId }: ScoreEntrySectionProps) => {
	const dispatch = useDispatch()

	// Column order comes from the sites slice, NOT from summary.sites. The
	// server re-sorts the summary by descending overallScore on every request,
	// and the summary refetches after every save — following that order would
	// make a column jump sideways mid-edit and steal focus from the input
	// under the caret.
	const sites = useSelector((state: any) => {
		const ids: string[] = state.evaluationSites?.byEvaluation?.[evalId] || []
		const entities = state.evaluationSites?.entities || {}
		return ids.map(id => entities[id]).filter(Boolean)
	})

	const criteria = useSelector((state: any) =>
		(state.scoring?.criteriaByEvaluation?.[evalId] || [])
			.slice()
			.sort((a: any, b: any) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
	)

	const summary = useSelector((state: any) => state.scoring?.summaryByEvaluation?.[evalId])

	// Flatten the summary once instead of a .find() per cell. Ids are ObjectIds
	// serialised to strings, so both sides are normalised with String() here
	// and every lookup below is a plain keyed read.
	const { scoreBySite, overallBySite } = useMemo(() => {
		const scores: Record<string, Record<string, number | null>> = {}
		const overall: Record<string, number | null> = {}
		for (const s of summary?.sites ?? []) {
			const siteKey = String(s.siteId)
			overall[siteKey] = s.overallScore ?? null
			const inner: Record<string, number | null> = {}
			// Per-site criterion rollups live on `criteria` (the top-level
			// `summary.criteria` is a count, not an array).
			for (const c of s.criteria ?? []) {
				inner[String(c.criterionId)] = c.avgScore ?? null
			}
			scores[siteKey] = inner
		}
		return { scoreBySite: scores, overallBySite: overall }
	}, [summary])

	const [saveError, setSaveError] = useState<string | null>(null)

	// "Suggest scores" per site (MYD-641), where Ask MDT is offered.
	const askAvailable = isAskMdtAvailable(useSelector((state: any) => state.userLogin?.userInfo))
	// The site stays set after closing: the Sheet must see `open` go false to
	// play its exit and give the page its scroll back, which unmounting it
	// would skip.
	const [suggestFor, setSuggestFor] = useState<{ id: string; name: string } | null>(null)
	const [suggestOpen, setSuggestOpen] = useState(false)

	// The rows the user kept, written one at a time through the ordinary score
	// endpoint as their own scores. Stops at the first failure and says how far
	// it got, rather than leaving a half-applied set unexplained.
	const applySuggestions = useCallback(
		async (siteId: string, scores: ScoreToApply[]) => {
			let saved = 0
			try {
				for (const score of scores) {
					// @ts-ignore — thunk
					await dispatch(upsertScore({ evalId, siteId, ...score })).unwrap()
					saved += 1
				}
			} catch (err: any) {
				throw new Error(`${saved} of ${scores.length} scores were saved, then: ${typeof err === 'string' ? err : 'a score failed to save'}.`)
			} finally {
				// @ts-ignore — refresh the matrix and ranking with whatever landed
				dispatch(fetchScoringSummary(evalId))
			}
		},
		[dispatch, evalId]
	)

	const handleScoreCommit = useCallback(
		(siteId: string, criterionId: string, value: number) => {
			if (!Number.isFinite(value) || value < 1 || value > 10) return
			// @ts-ignore — thunk
			const saving: any = dispatch(upsertScore({ evalId, siteId, criterionId, value }))
			saving
				.unwrap()
				.then(() => {
					setSaveError(null)
					// @ts-ignore — refresh ranking once the write lands
					dispatch(fetchScoringSummary(evalId))
				})
				// A rejected save used to refetch the summary anyway, so the cell
				// silently reverted and the user had no signal the score was lost.
				.catch((err: any) => setSaveError(typeof err === 'string' ? err : 'Failed to save score'))
		},
		[dispatch, evalId]
	)

	// Both empty states must return before the grid template is built —
	// `repeat(0, ...)` is invalid CSS and collapses the grid silently.
	if (sites.length === 0) {
		return (
			<Card>
				<SectionHeader />
				<CardContent>
					<p className="text-sm text-gray-400 text-center py-8">No sites to score. Add sites in the Sites tab first.</p>
				</CardContent>
			</Card>
		)
	}

	if (criteria.length === 0) {
		return (
			<Card>
				<SectionHeader />
				<CardContent>
					<p className="text-sm text-gray-400 text-center py-8">
						No criteria yet. Use the Criteria panel on the right to add at least one.
					</p>
				</CardContent>
			</Card>
		)
	}

	return (
		<Card>
			<SectionHeader saveError={saveError} />
			<CardContent>
				{/* --label-w is set here and inherited by the grid, so the label
				    column can narrow on mobile without a media-query hook. */}
				<div className="overflow-x-auto -mx-6 px-6 [--label-w:180px] lg:[--label-w:225px]">
					<div
						className="grid"
						style={{
							gridTemplateColumns: `var(--label-w) repeat(${sites.length}, minmax(${SITE_COL_MIN}px, 1fr))`,
							minWidth: '100%',
						}}
					>
						{/* Header row. Backgrounds on the sticky corner must be fully
						    opaque or scrolled cells show through it. */}
						<div className="sticky left-0 z-20 bg-gray-50 border-b border-r border-gray-200 px-3 py-2 flex items-end text-[10px] font-semibold uppercase tracking-wide text-gray-500">
							Criterion
						</div>
						{sites.map((site: any) => (
							<div key={site._id} className="bg-gray-50 border-b border-l border-gray-200 px-2 py-2 text-center">
								<div className="text-xs font-semibold text-gray-900 truncate" title={site.name}>
									{site.name}
								</div>
								{askAvailable && (
									<button
										type="button"
										onClick={() => {
											setSuggestFor({ id: String(site._id), name: site.name })
											setSuggestOpen(true)
										}}
										aria-label={`Suggest scores for ${site.name}`}
										title="Suggest scores with AI"
										className="mt-0.5 inline-flex items-center gap-0.5 text-[10px] font-medium text-primary hover:underline"
									>
										<Sparkles className="h-3 w-3" aria-hidden="true" /> Suggest
									</button>
								)}
							</div>
						))}

						{/* Total row — sits above the first criterion. border-b-2
						    separates the summary band from the editable band. */}
						<div className="sticky left-0 z-10 bg-blue-50 border-b-2 border-r border-gray-200 px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-primary">
							Total
						</div>
						{sites.map((site: any) => {
							const total = overallBySite[String(site._id)] ?? null
							return (
								<div
									key={site._id}
									className="bg-blue-50 border-b-2 border-l border-gray-200 px-2 py-2 text-center text-sm font-semibold text-primary"
								>
									{total != null ? total.toFixed(1) : <span className="text-gray-300">—</span>}
								</div>
							)
						})}

						{/* Criterion rows. `display: contents` generates no box, so
						    striping, borders and hover all live on the child cells. */}
						{criteria.map((c: any, rowIdx: number) => {
							const stripe = rowIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'
							return (
								<div className="contents group" key={c._id}>
									<div
										className={cn('sticky left-0 z-10 border-t border-r border-gray-200 px-3 py-1.5 min-w-0', stripe)}
									>
										{/* Wraps rather than truncates so the full criterion
										    name is always readable. Grid rows auto-size, so a
										    two-line name just makes that row taller and the
										    score cells stay vertically centred. */}
										<div
											className="text-xs font-medium text-gray-800 leading-snug break-words"
											title={c.description || c.name}
										>
											{c.name}
										</div>
										<div className="text-[10px] font-normal text-gray-400">{c.weight}%</div>
									</div>
									{sites.map((site: any) => (
										<div
											key={site._id}
											className={cn(
												'border-t border-l border-gray-100 px-1 py-1 flex items-center justify-center',
												stripe,
												'group-hover:bg-blue-50'
											)}
										>
											<DebouncedScoreInput
												value={scoreBySite[String(site._id)]?.[String(c._id)] ?? null}
												onCommit={val => handleScoreCommit(site._id, c._id, val)}
											/>
										</div>
									))}
								</div>
							)
						})}
					</div>
				</div>
				{suggestFor && (
					<ScoreSuggestionsSheet
						key={suggestFor.id}
						open={suggestOpen}
						onClose={() => setSuggestOpen(false)}
						evaluationId={evalId}
						site={suggestFor}
						onApply={applySuggestions}
					/>
				)}
			</CardContent>
		</Card>
	)
}

interface DebouncedScoreInputProps {
	value: number | null
	onCommit: (value: number) => void
}

const DebouncedScoreInput = ({ value, onCommit }: DebouncedScoreInputProps) => {
	const [local, setLocal] = useState<string>(value == null ? '' : String(value))
	const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
	const lastCommittedRef = useRef<string>(value == null ? '' : String(value))

	// Sync external state changes (e.g. summary refetch) into the local field
	// only when the user isn't mid-edit. The lastCommittedRef pin avoids
	// clobbering an in-progress edit if a refetch lands.
	useEffect(() => {
		const next = value == null ? '' : String(value)
		if (next !== lastCommittedRef.current) {
			setLocal(next)
			lastCommittedRef.current = next
		}
	}, [value])

	const scheduleCommit = useCallback(
		(raw: string) => {
			if (timerRef.current) clearTimeout(timerRef.current)
			timerRef.current = setTimeout(() => {
				const n = Number(raw)
				if (Number.isFinite(n) && n >= 1 && n <= 10) {
					lastCommittedRef.current = raw
					onCommit(n)
				}
			}, DEBOUNCE_MS)
		},
		[onCommit]
	)

	useEffect(() => {
		return () => {
			if (timerRef.current) clearTimeout(timerRef.current)
		}
	}, [])

	const flush = () => {
		if (timerRef.current) {
			clearTimeout(timerRef.current)
			timerRef.current = null
		}
		const n = Number(local)
		if (Number.isFinite(n) && n >= 1 && n <= 10 && local !== lastCommittedRef.current) {
			lastCommittedRef.current = local
			onCommit(n)
		}
	}

	return (
		<Input
			type="number"
			min={1}
			max={10}
			step={0.5}
			value={local}
			onChange={e => {
				const next = e.target.value
				setLocal(next)
				scheduleCommit(next)
			}}
			onBlur={flush}
			onKeyDown={e => {
				if (e.key === 'Enter') {
					e.currentTarget.blur()
				}
			}}
			// The grid scrolls horizontally, so a trackpad gesture over a focused
			// number input would otherwise silently change the score.
			onWheel={e => e.currentTarget.blur()}
			className="w-16 h-8 px-1 text-center text-sm"
		/>
	)
}

export default ScoreEntrySection
